import {
  DefinitionList,
  DefinitionListItem,
  DefinitionTerm,
  DefinitionValue,
  Section,
  SectionBody,
  SectionHeader,
  Skeleton,
} from "@apex/component-library";
import { Box, Stack, Text } from "@chakra-ui/react";
import { AxiosError } from "axios";
import { DateTime } from "luxon";

import { Currency, formatCurrency } from "../../utils/formatCurrency";
import { camelToKebabCase } from "../../utils/formatVariables";

export interface Data {
  label: string;
  value: string | number | JSX.Element;
  isNumeric?: boolean;
}

export interface DataSection {
  title: string;
  data: Data[];
  isLoading: boolean;
  error: AxiosError | null;
  skeletonLines?: number;
}

export const getErrorText = (error: AxiosError) => {
  const err = error as AxiosError<{
    errorDescription: string;
  }>;
  if (err) {
    const status = err.response?.status || err.request.status;
    const description = err.response?.data.errorDescription;
    switch (status) {
      case 403:
        return "Additional permissions are required.";
      case 500:
        return "System unavailable. Sorry, this system is temporarily unavailable.";
      default:
        return `The request failed. ${description}` || "";
    }
  }
};

export const getDataSection = ({
  title,
  data,
  isLoading,
  error,
  skeletonLines = 3,
}: DataSection) => {
  return (
    <Section>
      <SectionHeader variant="subsection">{title}</SectionHeader>
      {isLoading && (
        <Stack spacing={3} py={3}>
          {[...Array(skeletonLines)].map(() => (
            <Skeleton h="18px" />
          ))}
        </Stack>
      )}
      {error && (
        <Text pl={3} pb={3}>
          {getErrorText(error)}
        </Text>
      )}
      {data && !error && (
        <SectionBody>
          <DefinitionList>
            {data.map((data: Data) => {
              return (
                <DefinitionListItem>
                  <DefinitionTerm>{data.label}</DefinitionTerm>
                  <DefinitionValue
                    data-testid={`${camelToKebabCase(data.label)}-value`}
                  >
                    {data.isNumeric ? (
                      <Box w="50%" textAlign="right">
                        {data.value}
                      </Box>
                    ) : (
                      data.value
                    )}
                  </DefinitionValue>
                </DefinitionListItem>
              );
            })}
          </DefinitionList>
        </SectionBody>
      )}
    </Section>
  );
};

export const getCurrencyValue = (
  value: number,
  fractionDigits?: number
): string => {
  return formatCurrency(
    Currency.Usd,
    typeof value === "number" ? String(value) : null,
    fractionDigits
  );
};

export const marginProcessingExportFileName = () =>
  `AFS-MarginProcessing-Export_${DateTime.now().toFormat("yyyyMMdd")}`;

export const formatPercent = (value?: string | number) => {
  if (isNaN(Number(value))) {
    return "";
  }
  return `${Number(value)}%`;
};
